import { memo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Shield } from "lucide-react";
import categoryVapes from "@/assets/category-thca-vapes.webp";
import categoryHash from "@/assets/category-thca-hash.webp";
import categoryBuds from "@/assets/category-thca-buds.webp";
import categoryGuides from "@/assets/category-guides.webp";

interface TopicCard {
  title: string;
  description: string;
  href: string;
  image: string;
  alt: string;
}

const topicCards: TopicCard[] = [
  {
    title: "THCA Vapes",
    description: "Engångsvapes med upp till 99% renhet. Magic Sauce, Magic Farmers och Snozy.",
    href: "/kollektioner/thca-vapes",
    image: categoryVapes,
    alt: "THCA Vape Sverige – Köp premium engångsvapes online hos Vapebolag",
  },
  {
    title: "THCA Hash",
    description: "Traditionellt hash med 50% THCA. Bubble Hash, Charas och Afghan.",
    href: "/kollektioner/thca-hash",
    image: categoryHash,
    alt: "THCA Hash Sverige – Köp premium bubble hash och concentrat online",
  },
  {
    title: "THCA Buds",
    description: "Handodlade hampblommor med upp till 50% THCA. Indica, Sativa och Hybrid.",
    href: "/kollektioner/thca-buds",
    image: categoryBuds,
    alt: "THCA Buds Sverige – Köp lagliga THCA blommor och buds online",
  },
  {
    title: "Guider & Artiklar",
    description: "Lär dig om THCA, lagstiftning, dosering och skillnader mellan produkter.",
    href: "/blogg",
    image: categoryGuides,
    alt: "THCA guider och artiklar – Lär dig om laglig cannabis i Sverige",
  },
];

export const HomepageAuthorityHub = memo(() => {
  return (
    <section className="py-12 md:py-16 bg-card/20">
      <div className="container mx-auto px-4">
        {/* Authority Statement */}
        <div className="max-w-3xl mx-auto text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/30 mb-5">
            <Shield className="w-4 h-4 text-primary" />
            <span className="text-sm font-bold text-primary uppercase tracking-wide">Sveriges THCA-butik</span>
          </div>
          
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4 leading-tight">
            Utforska våra <span className="text-gradient">THCA-produkter</span>
          </h2>
          
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
            Vi erbjuder labbtestade THCA vapes, hash och buds. Snabb leverans till hela Sverige.
          </p>
        </div>

        {/* Topic Cards with images - Snozy-inspired but better */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-6xl mx-auto">
          {topicCards.map((card) => (
            <Link
              key={card.href}
              to={card.href}
              className="group relative rounded-xl overflow-hidden border border-border/50 hover:border-primary/40 transition-all duration-300 hover:scale-[1.02] aspect-[3/2] sm:aspect-[4/5]"
            >
              {/* Background image */}
              <img
                src={card.image}
                alt={card.alt}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              {/* Overlay gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
              
              {/* Content */}
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <h3 className="font-bold text-lg text-white mb-1">
                  {card.title}
                </h3>
                <p className="text-sm text-white/70 leading-relaxed mb-3 line-clamp-2">
                  {card.description}
                </p>
                <div className="flex items-center gap-1 text-primary text-sm font-semibold">
                  Handla
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Quick Stats */}
        <div className="flex flex-wrap justify-center gap-6 md:gap-10 mt-10 pt-6 border-t border-border/30">
          <div className="text-center">
            <p className="text-2xl md:text-3xl font-bold text-gradient">50+</p>
            <p className="text-sm text-muted-foreground">Produkter</p>
          </div>
          <div className="text-center">
            <p className="text-2xl md:text-3xl font-bold text-gradient">99%</p>
            <p className="text-sm text-muted-foreground">Max renhet</p>
          </div>
          <div className="text-center">
            <p className="text-2xl md:text-3xl font-bold text-gradient">1-3</p>
            <p className="text-sm text-muted-foreground">Dagars leverans</p>
          </div>
        </div>
      </div>
    </section>
  );
});

HomepageAuthorityHub.displayName = "HomepageAuthorityHub";
export default HomepageAuthorityHub;
